import type { IFolderDownloadItem } from "./FolderDownloadsDropdown";
import type { GraphqlMappedError } from "@/services/graphqlErrorService";
import type { ReactNode } from "react";

import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import Button from "@/components/ui/buttons/button";
import TopBanner from "@/components/ui/topBanner";
import { QR_FORMATS } from "@/constants/qrFormats";
import { QR_SIZES } from "@/constants/qrSizes";
import useDownloadFolderQrZip from "@/hooks/folders/useDownloadFolderQrZip";
import useMarkFolderReportDownloaded from "@/hooks/folders/useMarkFolderReportDownloaded";
import useUndownloadedFolderReports from "@/hooks/folders/useUndownloadedFolderReports";
import { useAuthService } from "@/services/authService";
import { notificationService } from "@/services/notificationService";

import FolderDownloadsDropdown from "./FolderDownloadsDropdown";
import StatusIcon from "./StatusIcon";

const NAMES_SHOWN_LIMIT = 3;
const MAX_FOLDER_NAME_LENGTH = 15;

function truncateFolderName(name: string) {
  return name.length > MAX_FOLDER_NAME_LENGTH
    ? `${name.slice(0, MAX_FOLDER_NAME_LENGTH)}…`
    : name;
}

function buildNamesList(names: string[]) {
  const shown = names
    .slice(0, NAMES_SHOWN_LIMIT)
    .map(truncateFolderName)
    .join(", ");
  const remaining = names.length - NAMES_SHOWN_LIMIT;

  return remaining > 0 ? `${shown}, +${remaining}` : shown;
}

function FolderDownloadTopBanner() {
  const { t } = useTranslation();
  const location = useLocation();
  const { undownloadedFolderReports: folders } = useAuthService();

  const handleMarkFolderReportDownloadedError = (e: GraphqlMappedError) => {
    notificationService.danger({ msg: e.errorTranslation });
  };

  const handleDownloadFolderQrZipError = (e: GraphqlMappedError) => {
    notificationService.danger({ msg: e.errorTranslation });
  };

  const { markFolderReportDownloaded } = useMarkFolderReportDownloaded({
    onError: handleMarkFolderReportDownloadedError,
  });

  const {
    refreshUndownloadedFolderReports,
    refreshUndownloadedFolderReportsLoading,
  } = useUndownloadedFolderReports();
  const { downloadFolderQrZip, loading: isRetryingDownload } =
    useDownloadFolderQrZip({
      onError: handleDownloadFolderQrZipError,
    });
  const [isDismissed, setIsDismissed] = useState(false);
  const [isDownloadsOpen, setIsDownloadsOpen] = useState(false);

  useEffect(() => {
    refreshUndownloadedFolderReports();
  }, [location.pathname, location.search, refreshUndownloadedFolderReports]);

  const onDownload = async (folder: IFolderDownloadItem) => {
    await markFolderReportDownloaded(folder.folderId);
    refreshUndownloadedFolderReports();
    window.location.href = folder.downloadUrl;
  };

  const onTryAgain = async (folder: IFolderDownloadItem) => {
    const result = await downloadFolderQrZip({
      folderId: folder.folderId,
      extension: folder.extension ?? QR_FORMATS[0].value,
      size: folder.size ?? QR_SIZES[0].value,
    });

    if (result?.url) {
      window.open(result.url, "_blank", "noopener,noreferrer");
    }
    refreshUndownloadedFolderReports();
  };

  if (!folders.length || isDismissed) return null;

  if (folders.length === 1) {
    const folder = folders[0];
    const isDone = folder.status === "COMPLETED";
    const isFailed = folder.status === "FAILED";
    const isPending = folder.status === "PENDING";

    return (
      <TopBanner onClose={() => setIsDismissed(true)}>
        <div className="d-flex align-items-start gap-1 flex-grow-1 fs-8">
          <StatusIcon
            status={isPending ? "pending" : isDone ? "done" : "failed"}
          />

          <div className="d-flex flex-wrap align-items-center gap-3">
            {isDone && (
              <div>
                <Trans
                  i18nKey="components.folderDownloadTopBanner.doneMessage"
                  components={{ b: <b /> }}
                />
              </div>
            )}

            {isFailed && (
              <div>
                <div className="fw-bold">
                  {t("components.folderDownloadTopBanner.failedTitle")}
                </div>
                <div className="text-secondary">
                  {t("components.folderDownloadTopBanner.failedInfo")}
                </div>
              </div>
            )}

            {isPending && (
              <div>
                <div className="fw-bold">
                  <Trans
                    i18nKey="components.folderDownloadTopBanner.preparingSingle"
                    values={{
                      folderName: truncateFolderName(folder.folderName),
                    }}
                    components={{ name: <span className="text-primary" /> }}
                  />
                </div>
                <div className="text-secondary">
                  {t("components.folderDownloadTopBanner.info")}
                </div>
              </div>
            )}

            {isFailed ? (
              <Button
                variant="outline-primary"
                className="px-4 text-nowrap"
                size="sm"
                isLoading={isRetryingDownload}
                onClick={() => onTryAgain(folder)}
              >
                {t("components.folderDownloadTopBanner.tryAgain")}
              </Button>
            ) : (
              <Button
                variant={isDone ? "primary" : "secondary"}
                className="fw-bold rounded-3 px-3 text-nowrap"
                size="sm"
                disabled={!isDone}
                onClick={() => onDownload(folder)}
              >
                {t("components.folderDownloadTopBanner.downloadFile")}
              </Button>
            )}
          </div>
        </div>
      </TopBanner>
    );
  }

  const ready = folders.filter((folder) => folder.status === "COMPLETED");
  const preparing = folders.filter((folder) => folder.status === "PENDING");
  const failed = folders.filter((folder) => folder.status === "FAILED");
  const allReady = ready.length === folders.length;

  const readyNames = buildNamesList(ready.map((folder) => folder.folderName));
  const preparingNames = buildNamesList(
    preparing.map((folder) => folder.folderName)
  );
  const failedNames = buildNamesList(failed.map((folder) => folder.folderName));

  let headline: ReactNode;
  if (failed.length === 0 && preparing.length === 0) {
    headline = (
      <>
        <span className="fw-bold">
          {t("components.folderDownloadTopBanner.allReady")}{" "}
        </span>
        <span className="fw-bold text-primary">{readyNames}.</span>
      </>
    );
  } else if (failed.length === 0 && ready.length === 0) {
    headline = (
      <span className="fw-bold">
        <Trans
          i18nKey="components.folderDownloadTopBanner.preparing"
          count={folders.length}
          values={{
            folderName: buildNamesList(
              folders.map((folder) => folder.folderName)
            ),
          }}
          components={{ name: <span className="text-primary" /> }}
        />
      </span>
    );
  } else {
    headline = (
      <span className="fw-bold">
        {ready.length > 0 && (
          <>
            {t("components.folderDownloadTopBanner.readyLabel")}{" "}
            <span className="text-primary">{readyNames}</span>.{" "}
          </>
        )}
        {preparing.length > 0 && (
          <>
            {t("components.folderDownloadTopBanner.preparingLabel")}{" "}
            <span className="text-primary">{preparingNames}</span>.{" "}
          </>
        )}
        {failed.length > 0 && (
          <>
            {t("components.folderDownloadTopBanner.failedLabel")}{" "}
            <span className="text-primary">{failedNames}</span>
          </>
        )}
      </span>
    );
  }

  const subtitle = allReady
    ? t("components.folderDownloadTopBanner.allReadyInfo")
    : ready.length > 0
      ? t(
          ready.length === 1
            ? "components.folderDownloadTopBanner.readyInfoOne"
            : "components.folderDownloadTopBanner.readyInfoOther"
        )
      : t("components.folderDownloadTopBanner.info");

  return (
    <TopBanner onClose={() => setIsDismissed(true)}>
      <div className="d-flex align-items-start gap-1 flex-grow-1 fs-8">
        <StatusIcon
          status={
            preparing.length > 0
              ? "pending"
              : ready.length > 0
                ? "done"
                : "failed"
          }
        />

        <div className="d-flex flex-wrap align-items-center gap-3">
          <div>
            <div>{headline}</div>
            <div className="text-secondary">{subtitle}</div>
          </div>

          <FolderDownloadsDropdown
            folders={folders}
            isOpen={isDownloadsOpen}
            onToggle={(nextIsOpen) => {
              setIsDownloadsOpen(nextIsOpen);
              if (nextIsOpen) refreshUndownloadedFolderReports();
            }}
            isRefreshing={refreshUndownloadedFolderReportsLoading}
            isRetryingDownload={isRetryingDownload}
            onDownload={onDownload}
            onTryAgain={onTryAgain}
          />
        </div>
      </div>
    </TopBanner>
  );
}

export default FolderDownloadTopBanner;
